import { motion } from 'framer-motion';
import { DOMAIN_NAMES, getScoreLevel } from '../../lib/api';

const PYRAMID_LAYERS = [
  'identity_alignment',
  'behavioral_execution',
  'belief_architecture',
  'cognitive_patterns',
  'brainwave_regulation',
  'brain_network_performance',
  'biological_stability'
];

const LAYER_DESCRIPTIONS = {
  identity_alignment: 'Purpose & Values',
  behavioral_execution: 'Actions & Habits',
  belief_architecture: 'Core Beliefs',
  cognitive_patterns: 'Thought Patterns',
  brainwave_regulation: 'Mental States',
  brain_network_performance: 'Neural Networks',
  biological_stability: 'Physical Foundation'
};

export default function SystemPyramid({ domainScores }) {
  if (!domainScores || domainScores.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        No data available
      </div>
    );
  }

  const scoreMap = Object.fromEntries(
    domainScores.map(ds => [ds.domain, ds.dysfunction_score])
  );

  return (
    <div className="py-4">
      <div className="flex flex-col items-center gap-1">
        {PYRAMID_LAYERS.map((layer, index) => {
          const score = scoreMap[layer] || 50;
          const level = getScoreLevel(score);
          const width = 50 + (index * 8); // Pyramid effect
          
          return (
            <motion.div
              key={layer}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className="relative group"
              style={{ width: `${width}%` }}
            >
              <div 
                className="h-12 rounded-lg flex items-center justify-between px-4 transition-all cursor-pointer hover:scale-[1.02]"
                style={{ 
                  backgroundColor: `${level.color}15`,
                  borderLeft: `3px solid ${level.color}`
                }}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">
                    {LAYER_DESCRIPTIONS[layer]}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate">
                    Level {7 - index}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span 
                    className="text-sm font-mono font-bold"
                    style={{ color: level.color }}
                  >
                    {score.toFixed(0)}
                  </span>
                </div>
              </div>

              {/* Tooltip on hover */}
              <div className="absolute left-full ml-4 top-1/2 -translate-y-1/2 hidden group-hover:block z-10">
                <div className="bg-popover border border-border rounded-lg p-3 shadow-lg whitespace-nowrap">
                  <p className="font-medium text-sm">{DOMAIN_NAMES[layer]}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Dysfunction: <span style={{ color: level.color }}>{score.toFixed(1)}</span>
                  </p>
                  <p className="text-xs mt-1">
                    <span 
                      className="px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: `${level.color}20`, color: level.color }}
                    >
                      {level.label}
                    </span>
                  </p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="mt-6 text-center">
        <p className="text-xs text-muted-foreground">
          Higher layers depend on lower layer stability
        </p>
        <div className="flex items-center justify-center gap-1 mt-2">
          <span className="text-[10px] text-muted-foreground">Foundation</span>
          <div className="h-px w-8 bg-border" />
          <span className="text-[10px] text-muted-foreground">Apex</span>
        </div>
      </div>
    </div>
  );
}
